import React from 'react';
import PropTypes from 'prop-types';

import { UploadedPhoto } from '../../prop-types';
import { DuplicatePhoto } from './duplicate-photo';
import { OriginalPhoto } from './original-photo';

/**
 * @param {object} props
 * @param {object} props.originalPhoto - Photo object for the original image.
 * @param {array} props.duplicatePhotos - Array of Photo objects as duplicates of original image.
 * @param {boolean} props.disabled - Whether prints builder updating is currently enabled or not.
 * @param {Set} props.selectedPhotos - Set of selected photo image_ids.
 * @param {Function} props.onPhotoSelect - Handler for selecting a photo.
 * @param {Function} props.onBulkSelect - Handle adding a list of duplicate photo ids.
 * @param {Function} props.onBulkDeSelect - Handle removing a list of duplicate photo ids.
 */
const DuplicatePhotoRow = ({
  originalPhoto,
  duplicatePhotos = [],
  disabled = false,
  selectedPhotos,
  onPhotoSelect,
  onBulkSelect,
  onBulkDeselect,
}) => {
  const allDuplicatesSelected = duplicatePhotos.every((p) =>
    selectedPhotos.has(p.image_id),
  );

  const duplicateImageIds = duplicatePhotos.map((p) => p.image_id);

  const handleSelectAll = () => {
    onBulkSelect(duplicateImageIds);
  };

  const handleClearSelection = () => {
    onBulkDeselect(duplicateImageIds);
  };

  return (
    <section className="duplicate-photos__row list-item pt-0">
      <div className="w-100 d-flex align-items-center justify-content-center justify-content-md-start p-2">
        <span className="d-none d-md-inline">
          Select/deselect all duplicates of this photo
        </span>
        <div className="ml-md-3">
          {allDuplicatesSelected ? (
            <button
              type="button"
              className="btn btn-secondary rounded-pill"
              onClick={handleClearSelection}
              disabled={disabled}>
              <span className="d-md-none">
                Deselect all duplicates of this photo
              </span>
              <span className="d-none d-md-inline">Clear Selection</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary rounded-pill"
              onClick={handleSelectAll}
              disabled={disabled}>
              <span className="d-md-none">
                Select all duplicates of this photo
              </span>
              <span className="d-none d-md-inline">Select All</span>
            </button>
          )}
        </div>
      </div>
      <div className="d-flex flex-wrap">
        <OriginalPhoto photo={originalPhoto} />

        {duplicatePhotos.map((duplicatePhoto) => (
          <DuplicatePhoto
            key={duplicatePhoto.image_id}
            photo={duplicatePhoto}
            isSelected={selectedPhotos.has(duplicatePhoto.image_id)}
            onPhotoClick={onPhotoSelect}
            disabled={disabled}
          />
        ))}
      </div>
    </section>
  );
};

DuplicatePhotoRow.propTypes = {
  originalPhoto: UploadedPhoto.isRequired,
  duplicatePhotos: PropTypes.arrayOf(UploadedPhoto),
  disabled: PropTypes.bool,
  selectedPhotos: PropTypes.instanceOf(Set).isRequired,
  onPhotoSelect: PropTypes.func.isRequired,
  onBulkSelect: PropTypes.func.isRequired,
  onBulkDeselect: PropTypes.func.isRequired,
};

export { DuplicatePhotoRow };
